// مشترک صفحات زیبایی CLN (AP-CLN-02): شناسه صفحات، مسیرها، کلیدهای Query، وضعیت پارامتر و جستجوی سروری خدمت و کالا.
import Chip from '@mui/material/Chip';
import {
  unwrap,
  type ClnInjectionPointRequest,
  type ClnParameterStatus,
  type ClnSessionParameterView,
  type ClnTreatmentPlanStatus,
} from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { faDigits, StatusChip, type StatusTone } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { num } from './common';

/** طرح درمان و پیشرفت (US-CLN-050، 051، 054). */
export const SCR_STAFF_503 = 'SCR-STAFF-503';
/** ثبت جلسه درمان (US-CLN-052، 053، 110 تا 112). */
export const SCR_STAFF_504 = 'SCR-STAFF-504';
/** تصاویر بالینی قبل/بعد (US-CLN-070، 071). */
export const SCR_STAFF_505 = 'SCR-STAFF-505';
/** عوارض: ثبت، پیگیری و گزارش (US-CLN-055). */
export const SCR_STAFF_507 = 'SCR-STAFF-507';

/**
 * فرم جلسه SCR-STAFF-504 در مواجهه Session یا Procedure (BR-CLN-051). نوع مواجهه از نوع خدمت نوبت پیش‌فرض می‌گیرد (C-AGENT-1100)، پس جلسه
 * روان‌درمانی (خدمت Session) هم Session است؛ مواجهه مشاوره یا یادداشت روان‌درمانی فرم جلسه درمان زیبایی و دستگاه را نشان نمی‌دهد.
 */
export function showsTreatmentSession(encounter: { type: string; counselingSession?: boolean | null }, note?: { kind?: string | null } | null): boolean {
  if (encounter.type !== 'Session' && encounter.type !== 'Procedure') {
    return false;
  }
  return !encounter.counselingSession && note?.kind !== 'Psychotherapy';
}

export const ADVERSE_PATH = '/desk/adverse-events';
export const planPath = (id: string) => `/desk/plans/${id}`;

export const beautyKeys = {
  plans: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'plans'] as const,
  plan: (tenantId: string | undefined, id: string) => ['clinical', tenantId, 'plan', id] as const,
  templates: (tenantId: string | undefined, q: string) => ['clinical', tenantId, 'plan-templates', q] as const,
  skin: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'skin'] as const,
  laser: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'laser-areas'] as const,
  adverse: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'adverse'] as const,
  adverseOne: (tenantId: string | undefined, id: string) => ['clinical', tenantId, 'adverse', id] as const,
  adverseReport: (tenantId: string | undefined, filter: string) => ['clinical', tenantId, 'adverse-report', filter] as const,
  images: (tenantId: string | undefined, patientId: string) => ['clinical', tenantId, 'patient', patientId, 'images'] as const,
  sessions: (tenantId: string | undefined, encounterId: string) => ['clinical', tenantId, 'encounter', encounterId, 'sessions'] as const,
  defaults: (tenantId: string | undefined, encounterId: string, key: string) => ['clinical', tenantId, 'encounter', encounterId, 'session-defaults', key] as const,
};

export const FITZPATRICK = ['I', 'II', 'III', 'IV', 'V', 'VI'] as const;
export const SKIN_TYPES = ['Normal', 'Dry', 'Oily', 'Combination', 'Sensitive'] as const;
export const HAIR_COLORS = ['Black', 'DarkBrown', 'LightBrown', 'Blond', 'Red', 'WhiteGrey'] as const;
export const HAIR_THICKNESS = ['Fine', 'Medium', 'Coarse'] as const;
export const ADVERSE_TYPES = ['Burn', 'Hyperpigmentation', 'Hypopigmentation', 'AllergicReaction', 'Infection', 'Bruising', 'Other'] as const;
export const ADVERSE_SEVERITIES = ['Mild', 'Moderate', 'Severe'] as const;
export const ADVERSE_OUTCOMES = ['UnderFollowUp', 'Resolved', 'ResolvedWithSequelae'] as const;
export const IMAGE_STAGES = ['Before', 'During', 'After', 'FollowUp'] as const;
export const IMAGE_ANGLES = ['Front', 'Left45', 'Right45', 'LeftProfile', 'RightProfile', 'Back', 'CloseUp'] as const;
export const INJECTION_DEPTHS = ['Intradermal', 'Subcutaneous', 'Intramuscular', 'Supraperiosteal'] as const;
export const INJECTION_TECHNIQUES = ['Bolus', 'Linear', 'Fanning', 'Microdroplet', 'Cannula'] as const;

const PLAN_TONES: Record<ClnTreatmentPlanStatus, StatusTone> = {
  Draft: 'pending',
  Proposed: 'booked',
  Accepted: 'confirmed',
  InProgress: 'in_service',
  OnHold: 'inactive',
  Completed: 'done',
  Discontinued: 'cancelled',
  Declined: 'cancelled',
};

export function PlanStatusChip({ status }: { status: ClnTreatmentPlanStatus }) {
  return <StatusChip tone={PLAN_TONES[status]} labelKey={`cln:plan_status.${status}`} />;
}

const PARAMETER_COLORS: Record<ClnParameterStatus, 'default' | 'success' | 'warning' | 'error'> = {
  InRange: 'success',
  OutsideRecommended: 'warning',
  OutsideAbsolute: 'error',
  Unknown: 'default',
};

/** وضعیت پارامتر نسبت به محدوده (AC-CLN-110.1). */
export function ParameterStatusChip({ status }: { status: ClnParameterStatus }) {
  const { t } = useTranslation('cln');
  if (status === 'Unknown') {
    return null;
  }
  return <Chip size="small" variant="outlined" color={PARAMETER_COLORS[status]} label={t(`cln:parameter_status.${status}`)} data-testid={`cln-param-status-${status}`} />;
}

/** نمایش کوتاه پارامترها (فهرست جلسات و نواحی لیزر). */
export function parameterSummary(parameters: readonly ClnSessionParameterView[]): string {
  return parameters
    .map((parameter) => {
      const value = parameter.value !== null && parameter.value !== undefined ? faDigits(Number(parameter.value)) : (parameter.text ?? '—');
      return `${parameter.name ?? parameter.key} ${value}${parameter.unit ? ` ${parameter.unit}` : ''}`;
    })
    .join(' · ');
}

/**
 * وضعیت مقدار در برابر محدوده مطلق و پیشنهادی (بازتاب سمت کاربر SessionParameter سرور؛ داوری نهایی با سرور است).
 */
export function rangeStatus(
  value: number | null,
  definition: { absoluteMin?: number | string | null; absoluteMax?: number | string | null; recommendedMin?: number | string | null; recommendedMax?: number | string | null },
): ClnParameterStatus {
  if (value === null || Number.isNaN(value)) {
    return 'Unknown';
  }
  const absMin = num(definition.absoluteMin);
  const absMax = num(definition.absoluteMax);
  if ((absMin !== null && value < absMin) || (absMax !== null && value > absMax)) {
    return 'OutsideAbsolute';
  }
  const recMin = num(definition.recommendedMin);
  const recMax = num(definition.recommendedMax);
  if (recMin === null && recMax === null) {
    return 'Unknown';
  }
  return (recMin !== null && value < recMin) || (recMax !== null && value > recMax) ? 'OutsideRecommended' : 'InRange';
}

/** جمع دوز نقاط تزریق به تفکیک واحد (AC-CLN-111.1؛ مقایسه با خدمت ثبت‌شده). */
export function doseTotals(points: readonly Pick<ClnInjectionPointRequest, 'dose' | 'unit'>[]): Record<'Unit' | 'Milliliter', number> {
  const totals = { Unit: 0, Milliliter: 0 };
  for (const point of points) {
    const dose = Number(point.dose);
    if (!Number.isNaN(dose)) {
      totals[point.unit] = Math.round((totals[point.unit] + dose) * 1000) / 1000;
    }
  }
  return totals;
}

/** ورودی متنی با Debounce برای جستجوی سروری. */
function useDebounced(input: string, delay = 250): string {
  const [value, setValue] = useState(input);
  useEffect(() => {
    const timer = window.setTimeout(() => setValue(input.trim()), delay);
    return () => window.clearTimeout(timer);
  }, [input, delay]);
  return value;
}

export interface ServiceOption {
  id: string;
  name: string;
  code: string;
}

/**
 * انتخاب خدمت فعال با جستجوی سروری (`q`)؛ فهرست کامل کاتالوگ هرگز بارگذاری نمی‌شود (فهرست‌های CAT فقط ۱۰۰ مورد نخست را برمی‌گردانند).
 * خدمات دیده‌شده نگه داشته می‌شوند تا نام مقدار انتخاب‌شده پس از تغییر جستجو هم نمایش داده شود.
 */
export function useCatalogServiceSearch(enabled = true) {
  const { tenantApi, session } = useTenantSession();
  const [input, setInput] = useState('');
  const q = useDebounced(input);
  const result = useQuery({
    queryKey: ['clinical', session?.tenantId, 'service-search', q],
    enabled,
    staleTime: 60_000,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/services', { params: { query: { q: q || undefined, status: 'Active', limit: 20 } } })),
  });
  const seen = useRef(new Map<string, ServiceOption>());
  const services: ServiceOption[] = (result.data?.items ?? []).map((item) => ({ id: item.id, name: item.name, code: item.code }));
  for (const service of services) {
    seen.current.set(service.id, service);
  }
  return {
    services,
    search: setInput,
    remember: (option: ServiceOption) => seen.current.set(option.id, option),
    find: (id: string) => seen.current.get(id),
    isFetching: result.isFetching,
  };
}

export interface ItemOption {
  id: string;
  name: string;
  code: string;
  tracksBatch: boolean;
}

/** جستجوی سروری کالای بالینی (محصول تزریقی) — INV `items?q=`. */
export function useItemSearch(enabled = true) {
  const { tenantApi, session } = useTenantSession();
  const [input, setInput] = useState('');
  const q = useDebounced(input);
  const result = useQuery({
    queryKey: ['clinical', session?.tenantId, 'item-search', q],
    enabled,
    staleTime: 60_000,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/items', { params: { query: { q: q || undefined, active: true, limit: 20 } } })),
  });
  const items: ItemOption[] = (result.data?.items ?? []).map((item) => ({ id: item.id, name: item.name, code: item.code, tracksBatch: item.tracksBatch }));
  return { items, search: setInput, isFetching: result.isFetching };
}

/** بچ‌های یک کالا؛ بچ منقضی یا قرنطینه انتخاب‌پذیر نیست (AC-CLN-111.2). */
export function useItemBatches(itemId: string | null) {
  const { tenantApi, session } = useTenantSession();
  return useQuery({
    queryKey: ['clinical', session?.tenantId, 'item-batches', itemId],
    enabled: Boolean(itemId),
    staleTime: 60_000,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/inventory/items/{id}/batches', { params: { path: { id: itemId ?? '' }, query: { activeOnly: false } } })),
  });
}
